import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useStyles } from './styles'
import { alpha, Avatar, Box, Chip, ListItem, Stack, Typography, useTheme } from '@mui/material'
import { formatUnixTimestamp } from '../../../util/dateTime'
import { LOGOS_BASE_URL } from '../../../assets/constants'
import { getColorForSymbol } from '../../../util/general'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { ExtraSmallButton } from '../../custom'
import { openChartInNewWindow } from '../../../util/common'
import { Broker_OrderType, Order, OrderStatus, OrderType, TransactionType } from '../../../proto/generated/order'
import { removeOrderFiredAlert } from '../../../reducer/orders.reducer'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoIcon from '@mui/icons-material/Info'
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material'
import BinIcon from '../../../assets/icons/BinIcon'

function StatusIcon({ status, transactionType }: { status: OrderStatus, transactionType: TransactionType }) {
	let severity: 'error' | 'warning' | 'success' | 'info' | 'buy' | 'sell'

	if([OrderStatus.OPEN, OrderStatus.INACTIVE, OrderStatus.TRIGGER_PENDING, OrderStatus.PENDING].includes(status)) {
		severity = transactionType===TransactionType.BUY ? 'buy' : 'sell'
	} else if(status===OrderStatus.EXECUTED) {
		severity = 'success'
	} else if([OrderStatus.CANCELLED, OrderStatus.FAILED, OrderStatus.REJECTED].includes(status)) {
		severity = 'error'
	}
	const getIcon = (severity) => {
		switch (severity) {
			case 'error':
				return <ErrorOutlineIcon color="error" />
			case 'warning':
				return <WarningAmberIcon color="warning" />
			case 'success':
				return <CheckCircleIcon color="success" />
			case 'info':
				return <InfoIcon color="info" />
			case 'buy':
				return <KeyboardArrowUpRounded sx={{color: theme => theme.palette.buy.main, height: '1.5em', width: '1.5em' }} />
			case 'sell':
				return <KeyboardArrowDownRounded sx={{color: theme => theme.palette.sell.main, height: '1.5em', width: '1.5em' }} />
			default:
				return null
		}
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '40px',
				background: theme => alpha(
					theme.palette[severity].main,
					0.2
				),
				borderTopLeftRadius: '4px',
				borderBottomLeftRadius: '4px',
				alignSelf: 'stretch'
			}}
		>
			{getIcon(severity)}
		</Box>
	)
}

const OrderAlertItem: React.FC<{ order: Order & { orderAlertId: string } }> = ({ order }) => {
	const { classes, cx } = useStyles()
	const dispatch = useDispatch()
	const theme = useTheme()
	const [expanded, setExpanded] = useState(false);

	const { orderAlertId, exchangeTimestamp, security, message, status, brokerOrderType, transactionType, quantity, price, triggerPrice, averageTradedPrice } = order
	const key = useMemo(() => getSecurityKey(security.exchange, security.code), [security])
	const [securityList, setSecurityList] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
	useEffect(() => {
		if (key) getSecurityAsync(key).then(setSecurityList)
		else setSecurityList(undefined)
	}, [key])

	const symbolsDisplayValue = useMemo(() => {
		if (!securityList) return ''
		return securityList.displayName || ''
	}, [securityList])

	const avatar = useMemo(() => {
		if (!securityList) return null
		let name = securityList.symbol || securityList.code
		if (!securityList.logoUrl) {
			return (
				<Avatar alt={'Icon'}
					color={getColorForSymbol(securityList?.symbol || securityList?.code || '', theme.palette.background.default)}
					className={classes.avatar}>
					{name
						.split(' ')
						.map((word) => word[0]?.toUpperCase())
						.slice(0, 2)
						.join('')}
				</Avatar>
			)
		}

		return <Avatar alt={'Icon'} src={`${LOGOS_BASE_URL}${securityList.logoUrl}.svg`} className={classes.avatar} />
	}, [securityList, key, theme.palette.background.default])

	const onClick = useCallback(() => {
		openChartInNewWindow(key)
	}, [key])

	const onFiredAlertRemoveClick = useCallback(() => {
		dispatch(removeOrderFiredAlert(orderAlertId))
	}, [orderAlertId])

	const displayName = useMemo(() => {
		if (!securityList) return ''
		return securityList?.symbol || securityList?.code || ''
	}, [securityList])

	const handleOnIconsClick = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation()
	}, [])

	const alertButtons = useMemo(() => {
		return (
			<Stack direction={'row'} justifyContent={'flex-end'} gap={0.5} className={cx(classes.displayOnHover, classes.minW0, classes.cursorPointer)}>
				<ExtraSmallButton
					onClick={onFiredAlertRemoveClick}
					color="inherit"
					aria-label="remove"
					data-event={'Order Fired Alert Removed'}
					data-event-alert-message={message}
					className={classes.hoveredButton}
				>
					<BinIcon className={classes.hoveredIcon} />
				</ExtraSmallButton>
			</Stack>
		)
	}, [message, classes, onFiredAlertRemoveClick])

	const hoverButtons = useMemo(() => {
		return (
			<Stack direction={'row'} justifyContent={'flex-end'} onClick={handleOnIconsClick}>
				{alertButtons}
			</Stack>
		)
	}, [alertButtons, classes])

	const alertName = useMemo(() => {
		let text = ''
		switch (brokerOrderType) {
			case Broker_OrderType.MARKET_ORDER:
				text = 'Market order'
				break
			case Broker_OrderType.LIMIT_ORDER:
				text = 'Limit order'
				break
			case Broker_OrderType.STOPLOSS_MARKET_ORDER:
				text = 'Stop loss market order'
				break
			case Broker_OrderType.STOPLOSS_ORDER:
				text = 'Stop loss order'
				break
			default:
				text = 'Order'
		}
		switch (status) {
			case OrderStatus.CANCELLED:
				text += ' canceled'
				break
			case OrderStatus.EXECUTED:
				text += ' executed'
				break
			case OrderStatus.FAILED:
				text += ' failed'
				break
			case OrderStatus.OPEN:
				text += ' placed'
				break
			case OrderStatus.REJECTED:
				text += ' rejected'
				break
			case OrderStatus.PENDING:
				text += ' pending'
		}
		text += ' on '
		return text
	}, [brokerOrderType, status])

	const nameAndMessage = useMemo(() => {
		return (
			<Stack className={classes.fullWidth}>
				<Stack className={cx(classes.fullWidth)} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }} direction={'row'}>
					<Stack direction={'row'} flexWrap={'wrap'}>
						<Typography fontWeight={500} fontSize={'14px'} mr={'4px'} noWrap variant='caption' className={cx('font-feature-num', classes.minW0)}>
							{alertName}
						</Typography>
						<Chip label={displayName} icon={avatar ?? undefined} size="small" onClick={onClick} className={classes.attributesChip} />
					</Stack>
					{hoverButtons}
				</Stack>
				{(message && message !== 'Order placed successfully') ? (
					<div style={{ lineHeight: 1.2 }}>
						<Typography variant='caption' className={cx('font-feature-num')} sx={{ lineHeight: 1.2}}>
							{expanded ? message : message.slice(0, 40)}
						</Typography>
						<Typography
							variant='caption'
							onClick={() => setExpanded(!expanded)}
							sx={{ cursor: 'pointer', color: theme=>theme.palette.buy.main, textTransform: "none", padding: 0, lineHeight: 1.2 }}
						>
							{expanded ? " Read less" : "...Read more"}
						</Typography>
					</div>
				) : null}
			</Stack>
		)
	}, [alertName, displayName, avatar, message, classes, onClick, hoverButtons, expanded])

	const attributes = useMemo(() => {
		const priceMultiplier = (securityList && securityList.priceMultiplier) ?? (security && security.priceMultiplier) ?? 100
		return (
			<Stack gap={0.5} direction="row" sx={{ justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
				<Stack direction={'row'}>
					<Typography fontWeight={600} fontSize={'12px'} sx={{
						background: theme => alpha(theme.palette[transactionType === TransactionType.BUY ? 'buy' : 'sell'].main, 0.2),
						px: '5px',
						borderRadius: '4px',
						color: theme => theme.palette[transactionType === TransactionType.BUY ? 'buy' : 'sell'].main
					}}>
						{
							`${transactionType === TransactionType.BUY ? "Buy" : "Sell"} ${quantity}`
						}
					</Typography>
					{
						Boolean(averageTradedPrice || triggerPrice || price) ?
							<Typography fontSize={'12px'} paddingLeft={'5px'}>
								{
									` at ${(averageTradedPrice || triggerPrice || price) / priceMultiplier}`
								}
							</Typography> : null
					}
				</Stack>
				{(
					<Typography variant={'caption'} color={'text.secondary'}>
						{formatUnixTimestamp(exchangeTimestamp ? exchangeTimestamp : Date.now(), true)}
					</Typography>
				)}
			</Stack>
		)
	}, [exchangeTimestamp, onClick, transactionType, quantity, averageTradedPrice, triggerPrice, price, securityList, security])

	const body = useMemo(() => {
		return (
			<ListItem
				key={orderAlertId}
				className={cx(classes.firedOrderContainer)}
				sx={{
					color: undefined,
					p: '5px !important',
					pr: '10px !important',
					minHeight: '60px'
				}}
			>
				{StatusIcon({status, transactionType})}
				<Stack direction={'column'} sx={{ flex: 1, maxWidth: '326px', height: '-webkit-fill-available', justifyContent: 'space-between', rowGap: '5px' }}>
					{nameAndMessage}
					{attributes}
				</Stack>
			</ListItem>
		)
	}, [alertButtons, transactionType, nameAndMessage, attributes, orderAlertId, classes.parentContainer, classes.primaryBordered, onClick])

	return body
}
export default memo(OrderAlertItem)
